import axios from 'axios';
import useSWR from 'swr';
import { useState } from 'react';

import { toast } from 'sonner';

import { formatAndThrowApiError } from '@/helpers/api';

import { resizeImage } from '@/app/document/_helpers';

// `refreshInterval` is opt-in and a number, not a predicate. SWR's polling effect lists
// `refreshInterval` among its dependencies, so a function held at module scope never
// re-runs it and can only stop a poll that is already going, never start one. The unit
// cost catalog's rebuild button is the caller that needs one: it flips this from 0 to an
// interval when `is_unit_cost_catalog_pending` goes true, and back to 0 when the build
// lands, which is what schedules and clears the timer. Everybody else gets the default 0
// and the same behaviour as before.
const useCompany = (refreshInterval = 0) => {
  const url = `/company`;
  const swr = useSWR(
    url,
    async () => {
      const result = await axios.get(url);
      return result.data;
    },
    { refreshInterval }
  );

  return swr;
};

const useCompanies = () => {
  const url = `/admin/company`;
  const swr = useSWR(url, async () => {
    const result = await axios.get(url);
    return result.data;
  });

  return swr;
};

const usePutCompany = () => {
  const putCompany = async (payload) => {
    try {
      const url = `/company`;
      const result = await axios.put(url, payload);
      return result.data;
    } catch (error) {
      toast.error('An error occurred updating company');
      formatAndThrowApiError(error);
    }
  };

  return putCompany;
};

const useUploadCompanyLogo = () => {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState();
  const putCompany = usePutCompany();

  const uploadCompanyLogo = async (file, onUploadSuccess) => {
    try {
      const resizedFile = await resizeImage(file, 600, 600);

      // Get the signed url from our server
      const signature = await axios.get(`/company/logo/upload-signature?fileType=${resizedFile.type}&fileName=${resizedFile.name}`);

      // Upload the file to the signed google url
      await axios.put(signature.data.signedUrl, resizedFile, {
        headers: {
          'Content-Type': resizedFile.type
        },
        withCredentials: false,
        onUploadProgress: (progressEvent) => {
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
        onError: (error) => {
          setError(error.message || 'An error occurred during upload');
          throw error;
        }
      });
      const newCompany = await putCompany({ logo_url: signature.data.publicUrl });
      onUploadSuccess(newCompany);
      setProgress(0);
      setError(null);
    } catch (error) {
      toast.error('An error occurred during upload');
      setError(error.message || 'An error occurred during upload');
      setProgress(0);
      throw error;
    }
  };

  return { progress, error, uploadCompanyLogo };
};

const useUploadCompanyIcon = () => {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState();
  const putCompany = usePutCompany();

  const uploadCompanyIcon = async (file, onUploadSuccess) => {
    try {
      const resizedFile = await resizeImage(file, 600, 600);

      // Get the signed url from our server (shares the company logo upload signature endpoint)
      const signature = await axios.get(`/company/logo/upload-signature?fileType=${resizedFile.type}&fileName=${resizedFile.name}`);

      // Upload the file to the signed google url
      await axios.put(signature.data.signedUrl, resizedFile, {
        headers: {
          'Content-Type': resizedFile.type
        },
        withCredentials: false,
        onUploadProgress: (progressEvent) => {
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
        onError: (error) => {
          setError(error.message || 'An error occurred during upload');
          throw error;
        }
      });
      const newCompany = await putCompany({ icon_url: signature.data.publicUrl });
      onUploadSuccess(newCompany);
      setProgress(0);
      setError(null);
    } catch (error) {
      toast.error('An error occurred during upload');
      setError(error.message || 'An error occurred during upload');
      setProgress(0);
      throw error;
    }
  };

  return { progress, error, uploadCompanyIcon };
};

const useIsCompanySSO = () => {
  const company = useCompany();
  return company.data.login_type === 'sso';
};

export { useCompany, useCompanies, usePutCompany, useIsCompanySSO, useUploadCompanyLogo, useUploadCompanyIcon };
